import { useCallback, useEffect, useMemo, useState } from "react";
import { ExternalLink, RefreshCw, Truck } from "lucide-react";
import { api } from "../../services/api";
import { formatUsDateTime } from "../../utils/timezone";
import { StatusPill } from "./AdminPanelParts";
import { ToastStack, useToast } from "./useToast";

function money(value, currency = "USD") {
  return `${currency} ${Number(value || 0).toFixed(2)}`;
}

function orderDestination(order) {
  const address = order?.shippingAddress || {};
  return [address.city, address.state, address.postalCode, address.country].filter(Boolean).join(", ") || "No address";
}

function formatDateTime(value) {
  return formatUsDateTime(value, "Not recorded");
}

const pickupCarriers = ["USPS", "UPS", "FedEx", "DHL"];

export function ShippingWorkspace({ embedded = false }) {
  const [shippoStatus, setShippoStatus] = useState(null);
  const [shippoOrders, setShippoOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [pickupCarrier, setPickupCarrier] = useState("USPS");
  const [pickupRequests, setPickupRequests] = useState([]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const { dismiss, toast, toasts } = useToast();

  const selectedOrder = useMemo(
    () => shippoOrders.find((order) => order._id === selectedOrderId),
    [selectedOrderId, shippoOrders]
  );

  const loadShippo = useCallback(async () => {
    setLoading(true);
    try {
      const [statusData, orderData, pickupData] = await Promise.all([
        api.get("/shippo/status"),
        api.get("/shippo/orders/ready"),
        api.get("/shippo/pickups"),
      ]);
      setShippoStatus(statusData);
      setShippoOrders(orderData.orders || []);
      setPickupRequests(pickupData.pickupRequests || []);
      setSelectedOrderId((current) => current || orderData.orders?.[0]?._id || "");
    } catch (err) {
      setShippoStatus({ enabled: false, error: err.message });
      setShippoOrders([]);
      setPickupRequests([]);
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadShippo();
  }, [loadShippo]);

  const fetchRates = async () => {
    if (!selectedOrderId) {
      toast("Select a paid order first", "warning");
      return;
    }
    setLoading(true);
    setRates([]);
    try {
      const data = await api.post(`/shippo/orders/${selectedOrderId}/rates`, {
        parcel: { length: 10, width: 8, height: 4 },
      });
      setRates(data.rates || []);
      toast(data.rates?.length ? "Shippo rates loaded" : "Shippo returned no rates", data.rates?.length ? "success" : "warning");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const buySelectedCheckoutLabel = async () => {
    if (!selectedOrder?.shippoRateId) {
      toast("This order does not have a checkout-selected Shippo rate", "warning");
      return;
    }
    await buyLabel({ objectId: selectedOrder.shippoRateId });
  };

  const buyLabel = async (rate) => {
    if (!selectedOrderId || !rate?.objectId) return;
    setLoading(true);
    try {
      const data = await api.post(`/shippo/orders/${selectedOrderId}/label`, {
        rateId: rate.objectId,
        labelFileType: "PDF_4x6",
      });
      toast(`Shippo label created. Tracking ${data.order.trackingNumber || "assigned"}`, "success");
      if (data.order.shippingLabelUrl) window.open(data.order.shippingLabelUrl, "_blank", "noopener,noreferrer");
      setRates([]);
      setSelectedOrderId("");
      await loadShippo();
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const requestPickup = async (carrier = pickupCarrier) => {
    try {
      const data = await api.post("/shippo/pickups", {
        carrier: String(carrier || pickupCarrier).toLowerCase(),
        pickupDate: new Date().toISOString(),
      });
      const nextPickupRequest = {
        ...data.pickupRequest,
        requestedAt: data.pickupRequest?.requestedAt || new Date().toISOString(),
      };
      setPickupRequests((current) => [nextPickupRequest, ...current].slice(0, 6));
      toast(data.warning || `${carrier} pickup request sent`, data.warning ? "warning" : "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const content = (
    <>
      <ToastStack dismiss={dismiss} toasts={toasts} />

      <div className={`${embedded ? "mb-4" : "mb-8"} flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between`}>
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-[#4391d1]">Shipping</p>
          <h1 className={embedded ? "text-2xl font-black text-gray-900" : "text-3xl font-black text-gray-900"}>Labels & Pickup</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-500">Create Shippo labels for ready orders and request the pickup from the same workflow.</p>
        </div>
        <button className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]" disabled={loading} onClick={loadShippo} type="button">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {shippoStatus?.error && (
        <div className="mb-4 rounded-2xl border border-red-100 bg-red-50 p-4 text-sm font-bold text-red-700">
          {shippoStatus.error}
        </div>
      )}

      <section className="grid gap-5 xl:grid-cols-[1.5fr_1fr]">
        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="text-lg font-black text-gray-900">Create Label</h2>
              <p className="mt-1 text-xs font-semibold text-gray-400">{shippoOrders.length} ready order{shippoOrders.length === 1 ? "" : "s"} waiting for label</p>
            </div>
            <StatusPill tone={shippoStatus?.enabled ? "good" : "warn"}>{shippoStatus?.enabled ? `Shippo ${shippoStatus?.mode || "connected"}` : "Shippo off"}</StatusPill>
          </div>

          {!shippoOrders.length && shippoStatus?.enabled && (
            <div className="mt-4 rounded-xl bg-gray-50 p-4 text-sm font-bold text-gray-500">
              No paid processing orders need a label.
            </div>
          )}

          <select
            className="mt-4 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold outline-none focus:border-[#4391d1]"
            disabled={!shippoOrders.length}
            onChange={(event) => {
              setSelectedOrderId(event.target.value);
              setRates([]);
            }}
            value={selectedOrderId}
          >
            <option value="">Select a paid order ready to ship</option>
            {shippoOrders.map((order) => (
              <option key={order._id} value={order._id}>
                {order.orderNumber} - {order.shippingAddress?.fullName || order.user?.name} - {orderDestination(order)}
              </option>
            ))}
          </select>

          {selectedOrder && (
            <div className="mt-4 rounded-xl bg-gray-50 p-4 text-sm font-semibold text-gray-600">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-black text-gray-900">{selectedOrder.orderNumber} / {selectedOrder.shippingAddress?.fullName || selectedOrder.user?.name}</p>
                  <p className="mt-1">{orderDestination(selectedOrder)}</p>
                </div>
                <StatusPill tone="good">${Number(selectedOrder.shipping || 0).toFixed(2)} charged</StatusPill>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {selectedOrder.shippoRateId && (
                  <button className="rounded-lg bg-[#4391d1] px-3 py-2 text-xs font-black text-white disabled:opacity-40" disabled={loading} onClick={buySelectedCheckoutLabel} type="button">
                    Buy Checkout Label
                  </button>
                )}
                <button className="rounded-lg bg-gray-950 px-3 py-2 text-xs font-black text-white disabled:opacity-40" disabled={loading || !selectedOrderId} onClick={fetchRates} type="button">
                  {loading ? "Loading..." : "Compare Rates"}
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 grid gap-3">
            {rates.map((rate) => (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 p-4" key={rate.objectId}>
                <div>
                  <p className="font-black text-gray-900">{rate.carrier} / {rate.service}</p>
                  <p className="text-xs font-semibold text-gray-500">{rate.durationTerms || `ETA ${rate.estimatedDays || "N/A"} days`}</p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill tone="info">{money(rate.amount, rate.currency)}</StatusPill>
                  <button className="inline-flex items-center gap-2 rounded-lg bg-[#4391d1] px-3 py-2 text-xs font-black text-white disabled:opacity-40" disabled={loading} onClick={() => buyLabel(rate)} type="button">
                    <ExternalLink className="h-3.5 w-3.5" />
                    Buy Label
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <span className="rounded-xl bg-blue-50 p-2 text-[#4391d1]"><Truck size={18} /></span>
            <div>
              <h2 className="text-lg font-black text-gray-900">Pickup</h2>
              <p className="mt-1 text-xs font-semibold text-gray-400">Request one carrier pickup after labels are ready.</p>
            </div>
          </div>

          <div className="mt-4 grid gap-3">
            <select
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold outline-none focus:border-[#4391d1]"
              onChange={(event) => setPickupCarrier(event.target.value)}
              value={pickupCarrier}
            >
              {pickupCarriers.map((carrier) => (
                <option key={carrier} value={carrier}>{carrier}</option>
              ))}
            </select>
            <button
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#4391d1] px-5 py-3 text-sm font-black text-white hover:bg-blue-600"
              onClick={() => requestPickup()}
              type="button"
            >
              <Truck className="h-4 w-4" />
              Request Pickup
            </button>
          </div>

          <div className="mt-4 rounded-xl bg-gray-50 p-4">
            {pickupRequests[0] ? (
              <div className="text-sm">
                <p className="font-black text-gray-900">{pickupRequests[0].carrier} pickup requested</p>
                <p className="mt-1 font-semibold text-gray-500">{formatDateTime(pickupRequests[0].requestedAt)} / {Number(pickupRequests[0].orders || 0)} ready orders</p>
              </div>
            ) : (
              <p className="text-sm font-bold text-gray-500">No pickup request recorded today.</p>
            )}
          </div>
        </div>
      </section>
    </>
  );

  if (embedded) return content;

  return (
    <div className="p-6 md:p-10">
      {content}
    </div>
  );
}

export default function Shippingcenter() {
  return <ShippingWorkspace />;
}
